// Volts forbindelse til /api/agent. Hvert kald har en hård deadline med AbortController: kommer
// svaret ikke i tide, får controlleren et fallback-svar, og rygmarven tager over uden at spilleren
// mærker det. `?agent=mock` giver en deterministisk transport uden net til tests og playtest.

import { DEFAULT_POLICY, clipLine, normalize, sanePolicy } from './protocol';
import type {
  AgentAnswer,
  AgentRequest,
  FallbackAnswer,
  FallbackReason,
  LineEvent,
  Mood,
  Plan,
  Policy,
  WireCandidate,
  WireState,
} from './protocol';

/** En request uden token: transporten sætter selv sessionen på. */
export type AgentCall = Omit<AgentRequest, 'token'>;

export interface Transport {
  readonly name: 'live' | 'mock' | 'spine';
  call(req: AgentCall, deadlineMs: number): Promise<AgentAnswer>;
  /** Hent en session i forvejen, så første rigtige kald ikke betaler for den. */
  warm(): Promise<void>;
}

/** Deadlines i ms. Jev skal svare inden ~0,85 s; Haiku må tænke lidt længere, den kører asynkront. */
export const DEADLINE = { act: 850, card: 850, line: 4000, plan: 4500, chat: 4500 } as const;

export function deadlineFor(kind: AgentCall['kind']): number {
  return kind === 'hands' ? DEADLINE.act : DEADLINE[kind];
}

export function isFallback(a: AgentAnswer): a is FallbackAnswer {
  return (a as FallbackAnswer).fallback === true;
}

function fb(reason: FallbackReason, line?: string): FallbackAnswer {
  return line ? { fallback: true, reason, line } : { fallback: true, reason };
}

// ---------------------------------------------------------------------------
// Live

type FetchFn = (input: string, init?: RequestInit) => Promise<Response>;

export interface LiveOptions {
  fetch?: FetchFn;
  now?: () => number;
  base?: string;
}

/** Så længe venter klienten med at spørge igen efter et budget- eller rate-svar. */
export const COOL_MS = { budget: 180_000, rate: 20_000 } as const;

export class LiveTransport implements Transport {
  readonly name = 'live' as const;
  private token: string | null = null;
  private coolUntil = 0;
  private coolReason: 'budget' | 'rate' = 'budget';
  private exp = 0;
  private pending: Promise<string | null> | null = null;
  private readonly fetchFn: FetchFn;
  private readonly now: () => number;
  private readonly base: string;

  constructor(opts: LiveOptions = {}) {
    this.fetchFn = opts.fetch ?? ((i, init) => fetch(i, init));
    this.now = opts.now ?? (() => Date.now());
    this.base = opts.base ?? '';
  }

  async warm(): Promise<void> {
    await this.session(4000);
  }

  private session(deadlineMs: number): Promise<string | null> {
    // Fornyes et minut før udløb.
    if (this.token && this.now() / 1000 < this.exp - 60) return Promise.resolve(this.token);
    if (this.pending) return this.pending;
    this.pending = (async () => {
      const ac = new AbortController();
      const timer = setTimeout(() => ac.abort(), deadlineMs);
      try {
        const r = await this.fetchFn(`${this.base}/api/session`, { method: 'POST', signal: ac.signal });
        const j = (await r.json()) as { ok?: boolean; token?: unknown; exp?: unknown };
        if (j.ok && typeof j.token === 'string' && typeof j.exp === 'number') {
          this.token = j.token;
          this.exp = j.exp;
          return this.token;
        }
        return null;
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
        this.pending = null;
      }
    })();
    return this.pending;
  }

  async call(req: AgentCall, deadlineMs: number): Promise<AgentAnswer> {
    const t0 = this.now();
    // Brugt budget eller rate limit: spørg ikke serveren igen før pausen er gået (rygmarven spiller imens).
    if (t0 < this.coolUntil) return fb(this.coolReason);
    const token = await this.session(deadlineMs);
    if (!token) return fb('session');
    const left = deadlineMs - (this.now() - t0);
    if (left <= 0) return fb('timeout');
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), left);
    try {
      const r = await this.fetchFn(`${this.base}/api/agent`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...req, token }),
        signal: ac.signal,
      });
      const j = (await r.json()) as AgentAnswer;
      if (isFallback(j) && j.reason === 'session') {
        // Udløbet eller ny IP: næste kald henter en frisk session.
        this.token = null;
      }
      if (isFallback(j) && (j.reason === 'budget' || j.reason === 'rate')) {
        this.coolReason = j.reason;
        this.coolUntil = this.now() + COOL_MS[j.reason];
      }
      return j;
    } catch (e) {
      return fb(ac.signal.aborted || (e as Error)?.name === 'AbortError' ? 'timeout' : 'offline');
    } finally {
      clearTimeout(timer);
    }
  }
}

// ---------------------------------------------------------------------------
// Mock: deterministisk, uden net og uden Math.random

/** Lille stabil hash, så mock-svar varierer med state men altid er ens for ens input. */
export function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Softmax over reservebottens scorer. Temperaturen gør toppen tydelig, men ikke enerådende. */
export function softmax(cands: readonly WireCandidate[], temp = 12): Record<string, number> {
  const max = Math.max(...cands.map((c) => c.score));
  const e: Record<string, number> = {};
  for (const c of cands) e[c.id] = Math.exp((c.score - max) / temp);
  return normalize(e);
}

const MOCK_LINES: Record<LineEvent, readonly string[]> = {
  start: ['Hej makker. Jeg tager kablerne, du tager æren.', 'Klar! Headsettet sidder, og kaffen er varm.'],
  built: ['Ny ledning. Strømmen har fået en genvej.', 'Bygget! Den holder, tror jeg nok.'],
  upgraded: ['Mere plads på ledningen. Strømmen jubler.', 'Opgraderet. Nu kan den tåle lidt mere.'],
  reinforced: ['Masterne er spændt fast. Kom bare an, storm.', 'Forstærket. Stormen må finde en anden.'],
  repaired: ['Lappet sammen. Næsten som ny.', 'Holdet har klaret den. Lyset er tilbage.'],
  saved: ['Puh. Den var tæt på.', 'Reddet i sidste sekund. Sådan!'],
  blackout: ['Av. Der gik lyset. Vi får det tilbage.', 'Mørkt derude. Jeg kigger på det.'],
  storm: ['Storm på vej. Jeg holder øje med masterne.', 'Blæsten tager til. Lad os være klar.'],
  card: ['Et kort! Jeg har et forslag.', 'Nyt valg. Jeg ville gøre sådan her.'],
  year: ['Et år mere. Nettet vokser med os.', 'Godt år, makker. Videre!'],
  won: ['Vi klarede det! High five med kablet.', 'Banen er vores. Godt spillet!'],
  lost: ['Den gik ikke. Næste gang tager vi den.', 'Øv. Men vi lærte noget om strøm.'],
  idle: ['Jeg sparer lidt op. Det giver ro.', 'Stille nu. Jeg holder øje.'],
  player_built: ['Flot ledning! Jeg tager den næste.', 'God idé. Den hjælper os begge.'],
};

const MOOD_OF: Partial<Record<LineEvent, Mood>> = {
  saved: 'proud',
  won: 'proud',
  blackout: 'worried',
  storm: 'worried',
  lost: 'calm',
  start: 'happy',
  player_built: 'cheeky',
};

/** Trusler med dansk navn til svar på spørgsmål. */
const THREAT_DA: Partial<Record<string, string>> = {
  storm: 'en storm',
  digger: 'en gravko',
  anchor: 'en ankerbåd',
  protest: 'en naboprotest',
  spike: 'en spidsbølge',
  heatwave: 'en hedebølge',
  icing: 'isslag',
  dunkelflaute: 'mørke og vindstille',
  transformer: 'en trafofejl',
};

/**
 * Et spørgsmål får et svar, ikke en ordre (IGN runde 1: "hvad skal jeg gøre?" gav "Modtaget: byerne
 * først."). Svaret bygger kun på det, Volt kan se. null: det er ikke et spørgsmål.
 */
export function questionReply(text: string, s: WireState, wish = false): string | null {
  const t = text.toLowerCase().trim();
  // "Hvorfor/hvad/hvordan ..." er altid et spørgsmål. "Kan du tage vest?" er et ønske med spørgsmålstegn.
  const inquiry = /^(hvad|hvorfor|hvordan|hvornår|hvem|hvilk|hvor)/.test(t);
  if (!inquiry && !(t.endsWith('?') && !wish)) return null;
  const worst = s.cities.find((c) => c.clock > 0 || c.srv < c.dem - 0.5);
  const threat = [...s.threats].filter((x) => x.in >= 0).sort((a, b) => a.in - b.in)[0];
  if (/storm|vejr|kommer|truer/.test(t)) {
    if (!threat) return 'Intet på vej lige nu. Nyd roen.';
    return `Om ${Math.max(1, Math.round(threat.in))} s kommer ${THREAT_DA[threat.kind] ?? 'noget'}. Jeg holder øje.`;
  }
  if (/penge|kasse|råd|raad|budget|kr\b/.test(t)) {
    const kr = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(Math.trunc(s.budget));
    return s.budget < 15 ? `Kun ${kr} kr i kassen. Vi sparer lidt.` : `Vi har ${kr} kr i kassen.`;
  }
  if (/grøn|groen|kul|værk|vind|sol/.test(t)) return `Nettet er ${Math.round(s.green)} % grønt. Vind og sol først, kul til sidst.`;
  if (worst) return `${worst.name} mangler strøm. Giv byen en vej mere, så tager jeg resten.`;
  if (threat) return `Alt kører. Om ${Math.max(1, Math.round(threat.in))} s kommer ${THREAT_DA[threat.kind] ?? 'noget'}.`;
  return 'Alt kører. Byg videre mod en ny by, eller spar op til næste storm.';
}

/** Alt, policyFromText forstår. En del af beskeden uden noget af det her bliver nævnt i svaret. */
const KNOWN =
  /nulstil|glem|normal|som før|vest|øst|oest|nord|syd|byg (løs|loes|igen|bare|gerne|mere|videre)|bare byg|brug (penge|pengene|kassen)|spar|penge|\bgem|storm|grøn|groen|vind|sol|\bbyer(ne)?\b|\bbyen\b|\bby\b|\blys\b|sikker|forsigtig|robust|kort|byg ikke|ingen nye/;

/** Spillerens instruks som politik. Bruges af chips, mock og som sikkerhedsnet uden Haiku. */
export function policyFromText(text: string, current: Policy = DEFAULT_POLICY): { policy: Policy; reply: string } {
  const t = text.toLowerCase();
  if (/nulstil|glem|normal|som før/.test(t)) return { policy: { ...DEFAULT_POLICY, avoid: [] }, reply: 'Nulstillet. Jeg spiller frit igen.' };
  const p: Policy = { ...current, avoid: [...current.avoid] };
  const said: string[] = [];
  const region = /vest/.test(t) ? 'west' : /øst|oest/.test(t) ? 'east' : /nord/.test(t) ? 'north' : /syd/.test(t) ? 'south' : null;
  if (region) {
    p.focus_region = region;
    said.push({ west: 'vest', east: 'øst', north: 'nord', south: 'syd' }[region]);
  }
  // "Byg løs": Volt står ikke stille og sparer, heller ikke når Haikus plan siger "hold kassen".
  const spree = /byg (løs|loes|igen|bare|gerne|mere|videre)|bare byg|brug (penge|pengene|kassen)/.test(t);
  const save = !spree && /spar|penge|\bgem/.test(t);
  if (spree) {
    if (!p.avoid.includes('wait')) p.avoid.push('wait');
    p.avoid = p.avoid.filter((a) => a !== 'build' && a !== 'spend');
    if (p.priority === 'money') p.priority = 'balanced';
    p.save_for = 'nothing';
    said.push('jeg bygger løs');
  }
  if (save) {
    // Spar betyder spar: ingen nye udgifter, medmindre en by ellers går i sort (se spendsAgainst).
    p.priority = 'money';
    p.save_for = 'nothing';
    p.avoid = p.avoid.filter((a) => a !== 'wait');
    said.push('sparer op');
  }
  if (/storm/.test(t) && /jeg tager|min|mig/.test(t)) {
    if (!p.avoid.includes('storm')) p.avoid.push('storm');
    said.push('stormen er din');
  } else if (/storm/.test(t)) {
    // "Spar til stormen": spar, men masterne må gerne koste.
    if (!save) p.priority = 'safety';
    p.save_for = 'storm';
    said.push('klar til storm');
  }
  if (/grøn|groen|vind|sol/.test(t)) {
    p.priority = 'green';
    said.push('grønt');
  }
  // Ikke /\bby/: det fanger "bygger" og "byg ikke".
  if (/\bbyer(ne)?\b|\bbyen\b|\bby\b|\blys\b/.test(t)) {
    p.priority = 'cities';
    said.push('byerne først');
  }
  if (/sikker|forsigtig|robust/.test(t)) {
    p.priority = 'safety';
    said.push('sikkert');
  }
  if (/kort/.test(t) && /jeg tager|min|mig/.test(t)) {
    if (!p.avoid.includes('cards')) p.avoid.push('cards');
    said.push('kortene er dine');
  }
  if (/byg ikke|ingen nye/.test(t)) {
    if (!p.avoid.includes('build')) p.avoid.push('build');
    p.avoid = p.avoid.filter((a) => a !== 'wait');
    said.push('ingen nye ledninger');
  }
  // Sig ærligt, hvad der ikke blev forstået (playtest runde 3: "spar op og byg kabler ved kysten"
  // gav kun "Modtaget: sparer op.").
  const missed = t
    .split(/,|;|\.|!|\?|\bog\b|\bmen\b/)
    .map((c) => c.trim())
    .filter((c) => c.length > 2 && !KNOWN.test(c));
  const miss = missed.length > 0 ? ` "${missed[0]?.slice(0, 40)}" kan jeg ikke endnu.` : '';
  if (said.length === 0) return { policy: current, reply: missed.length > 0 ? 'Den forstod jeg ikke. Prøv fx "spar op", "byerne først" eller "tag vest".' : 'Forstået. Jeg gør mit bedste.' };
  const reply = `Modtaget: ${said.join(', ')}.${miss}`;
  return { policy: sanePolicy(p), reply: reply.charAt(0).toUpperCase() + reply.slice(1) };
}

/** En plan ud fra state: fokus på regionen med de mørkeste ure, ellers balance. */
export function mockPlan(s: WireState): Plan {
  const worst = s.cities[0];
  const storm = s.threats.some((t) => t.kind === 'storm' && t.in > 0);
  const region = worst && worst.clock > 10 ? worst.region : 'all';
  const priority = storm ? 'safety' : worst && worst.dem > worst.srv ? 'cities' : s.green < 40 ? 'green' : 'balanced';
  const save_for = storm ? 'storm' : s.budget < 15 ? 'new_city' : 'nothing';
  // Med roller siger noten, hvem der gør hvad: masterne er driftsvagtens job, nye ledninger planlæggerens.
  const masts = s.role === 'planner' ? 'Storm på vej: forstærk masterne, jeg bygger' : s.role === 'drift' ? 'Storm på vej, jeg tager masterne' : 'Storm på vej, masterne først';
  const short = worst && worst.clock > 10 ? (s.role === 'drift' ? `${worst.name} mangler en ny ledning` : `${worst.name} mangler strøm`) : 'Byg roligt videre';
  const note = storm ? masts : short;
  return { focus_region: region, priority, save_for, note: note.slice(0, 60) };
}

export interface MockOptions {
  /** Kunstig svartid i ms (standard 180 for Jev, 600 for Haiku). Over deadline giver timeout. */
  latency?: (req: AgentCall) => number;
  /** Tving en bestemt confidence (tests). */
  confidence?: number;
  /** Ventefunktion (tests kan gøre den øjeblikkelig). */
  sleep?: (ms: number) => Promise<void>;
}

export class MockTransport implements Transport {
  readonly name = 'mock' as const;
  calls: AgentCall[] = [];
  private readonly opts: MockOptions;
  constructor(opts: MockOptions = {}) {
    this.opts = opts;
  }

  async warm(): Promise<void> {}

  answer(req: AgentCall): AgentAnswer {
    const kind = req.kind === 'hands' ? 'act' : req.kind;
    const ms = this.latency(req);
    if (kind === 'act' || kind === 'card') {
      const cands = req.candidates ?? [];
      if (cands.length < 2) return fb('invalid');
      const probs = softmax(cands);
      let choice = cands[0]!.id;
      for (const c of cands) if ((probs[c.id] ?? 0) > (probs[choice] ?? 0)) choice = c.id;
      const confidence = this.opts.confidence ?? Math.round((probs[choice] ?? 0) * 100) / 100;
      return { ok: true, source: 'jev', choice, confidence, probabilities: probs, ms };
    }
    if (kind === 'line') {
      const ev = req.event ?? 'idle';
      const list = MOCK_LINES[ev];
      const line = list[hash(`${ev}|${req.about ?? ''}|${req.state.year}|${req.state.budget}`) % list.length]!;
      return { ok: true, source: 'haiku', line: clipLine(line), mood: MOOD_OF[ev] ?? 'happy', ms };
    }
    if (kind === 'plan') {
      const plan = mockPlan(req.state);
      return { ok: true, source: 'haiku', plan, line: clipLine(plan.note), ms };
    }
    const before = req.state.policy ?? DEFAULT_POLICY;
    const { policy, reply } = policyFromText(req.text ?? '', before);
    // Et spørgsmål får et svar og lader planen stå.
    const q = questionReply(req.text ?? '', req.state, policy !== before);
    return { ok: true, source: 'haiku', reply: clipLine(q ?? reply), policy: q ? before : policy, ms };
  }

  private latency(req: AgentCall): number {
    if (this.opts.latency) return this.opts.latency(req);
    return req.kind === 'act' || req.kind === 'hands' || req.kind === 'card' ? 180 : 600;
  }

  async call(req: AgentCall, deadlineMs: number): Promise<AgentAnswer> {
    this.calls.push(req);
    const ms = this.latency(req);
    const sleep = this.opts.sleep ?? ((n: number) => new Promise<void>((r) => setTimeout(r, n)));
    if (ms > deadlineMs) {
      await sleep(deadlineMs);
      return fb('timeout');
    }
    await sleep(ms);
    return this.answer(req);
  }
}

/**
 * Kun rygmarv (`?p2=bot`): Jev svarer aldrig, så reservebotten vælger hver gang. Replikker og planer
 * kommer fra de lokale mock-svar, så Volt stadig snakker, helt uden net.
 */
export class SpineTransport implements Transport {
  readonly name = 'spine' as const;
  private readonly mock = new MockTransport();
  async warm(): Promise<void> {}
  async call(req: AgentCall): Promise<AgentAnswer> {
    if (req.kind === 'hands' || req.kind === 'card') return fb('offline');
    return this.mock.answer(req);
  }
}

export function makeTransport(mode: 'mock' | 'live' | 'spine'): Transport {
  if (mode === 'spine') return new SpineTransport();
  return mode === 'mock' ? new MockTransport() : new LiveTransport();
}
